import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Each door is its own installable app with its OWN scope — Chrome refuses to install a second app
// whose scope overlaps one already installed, so the apps live under /admin, /app/,
// /portal/driver/, /portal/client/ and the operator's own /ride/<handle>/.
const DOORS = [
  { match: /^\/admin/, manifest: "/manifest-admin.json", title: "LimoLinkX — owner console" },
  { match: /^\/(portal\/driver|drivers|driver\/trip)/, manifest: "/manifest-driver.json", title: "LimoLinkX — chauffeur app" },
  { match: /^\/(app|office|login|signup)/, manifest: "/manifest-office.json", title: "LimoLinkX — dispatch office" },
  { match: /^\/(portal\/client|clients)/, manifest: "/manifest.json", title: "LimoLinkX — passenger portal" },
  // A passenger token page belongs to an operator we cannot name from the URL alone. It redirects
  // into /ride/<handle>/ as soon as the trip loads, so advertise NO manifest until then — offering
  // another app's manifest here is how a passenger ended up installing the wrong app.
  { match: /^\/(ride|book|r|track|receipt|pay|card|statement|corporate|winback|booking)\b/, manifest: null, title: "LimoLinkX" },
  // Everything else is the marketing site. It used to advertise the PASSENGER manifest, so an owner
  // installing from the home page ended up with an icon that opened the passenger portal.
  { match: /.*/, manifest: "/manifest-office.json", title: "LimoLinkX — Manage. Book. Grow." },
];

// The operator's own ride app lives under /ride/<handle>, so every page inside it must advertise
// THEIR branded manifest. This component is the only place that touches the manifest link.
const BRANDED = /^\/(?:ride|book)\/([^/]+)/;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Same origin as the page wherever possible: a manifest served from another origin resolves its
// scope against THAT origin, which puts every page of the installed app out of scope.
const apiBase = () => {
  try {
    return new URL(BACKEND).origin === window.location.origin ? "" : BACKEND;
  } catch {
    return BACKEND;
  }
};

export const AppManifest = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const door = DOORS.find((d) => d.match.test(pathname));
    if (!door) return;
    const branded = BRANDED.exec(pathname);
    const href = branded
      ? `${apiBase()}/api/public/company/${encodeURIComponent(branded[1])}/manifest.webmanifest`
      : door.manifest;
    let link = document.querySelector('link[rel="manifest"]');
    if (!href) {
      if (link) link.remove();
      document.title = door.title;
      return;
    }
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== href) link.setAttribute("href", href);
    // A branded page titles itself with the operator's own name once it loads.
    if (!branded) document.title = door.title;
  }, [pathname]);
  return null;
};
